import { useEffect, useState } from 'react';

/**
 * Reactive media-query hook. Subscribes to the underlying `matchMedia`
 * list once per query string and tears the listener down on unmount.
 *
 * Use the named helpers (`useIsMobile`, `useIsCoarsePointer`) for the
 * common Atlas breakpoints — the raw `useMediaQuery` is exported for
 * the rare custom-query case (print, dark-scheme, etc.).
 *
 * SSR-safe: returns `false` on the first render when `window` is
 * undefined, then flips to the real value on the client mount. The
 * mobile branches in DataTable / MobileTableShell tolerate the brief
 * desktop-first paint because the cards / list mount in the same DOM
 * slot regardless.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    // Re-sync on mount in case the initial state was stale (e.g.
    // viewport rotated between SSR snapshot and hydration).
    setMatches(mql.matches);
    // Safari < 14 only exposes the deprecated `addListener` API. We
    // feature-detect so this keeps working on older iPads.
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, [query]);

  return matches;
}

/**
 * Atlas's mobile breakpoint is Tailwind's `md` (768 px). Everything
 * below renders as a single-column mobile experience; everything at
 * or above renders the desktop tables / wide layouts. Centralising the
 * literal here keeps the table framework, BottomSheet, and PageHeader
 * aligned on one boundary.
 */
export function useIsMobile(): boolean {
  return useMediaQuery('(max-width: 767px)');
}

/**
 * Coarse pointer = touch device (phone / tablet) — distinct from
 * "mobile width" because a large tablet in landscape passes
 * `useIsMobile() === false` but still wants touch-friendly hit targets
 * and no hover-only affordances. Used by MobileListCard's swipe + the
 * 44 px tap-target minimum on toolbar pills.
 */
export function useIsCoarsePointer(): boolean {
  return useMediaQuery('(pointer: coarse)');
}
